import { MailOutlined, SendOutlined } from "@ant-design/icons";
import {
  type MailAttachmentPayload,
  type MailSendPayload,
  type NotificationChannel,
  type NotificationSendPayload,
  sendNotification,
} from "@features/mail/api";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import DrawerForm from "@shared/ui/drawer-form";
import { setStoredUserEmail } from "@shared/userStorage";
import { App as AntdApp, Checkbox, Form, Input, Space, Tag, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";

interface MailModalProps {
  open: boolean;
  onClose: () => void;
  defaultEmail?: string;
  defaultTelegramId?: number;
  defaultTelegramUsername?: string;
  defaultUserId?: string;
  defaultSubject?: string;
  defaultBody?: string;
  getAttachments?: () => Promise<MailAttachmentPayload[]>;
  onSubmit?: (payload: NotificationSendPayload) => Promise<void>;
}

export default function MailModal({
  open,
  onClose,
  defaultEmail,
  defaultTelegramId,
  defaultTelegramUsername,
  defaultUserId,
  defaultSubject,
  defaultBody,
  getAttachments,
  onSubmit,
}: MailModalProps) {
  const [loading, setLoading] = useState(false);
  const hasTelegram = Boolean(defaultTelegramId);
  const { message } = AntdApp.useApp();

  const defaultSelection: NotificationChannel[] = useMemo(() => {
    if (hasTelegram) {
      return defaultEmail ? ["email", "telegram"] : ["telegram"];
    }
    return ["email"];
  }, [hasTelegram, defaultEmail]);

  const [channels, setChannels] = useState<NotificationChannel[]>(defaultSelection);

  useEffect(() => {
    if (open) {
      setChannels(defaultSelection);
    }
  }, [open, defaultSelection]);

  const emailRequired = channels.includes("email");

  const handleSend = async (values: MailSendPayload) => {
    try {
      setLoading(true);
      const attachments = getAttachments ? await getAttachments() : undefined;
      const payload: NotificationSendPayload = {
        user_id: defaultUserId,
        to: values.to,
        telegram_id: defaultTelegramId,
        subject: values.subject,
        body: values.body,
        attachments,
        channels,
      };
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await sendNotification(payload);
      }
      if (values.to) {
        setStoredUserEmail(values.to);
      }
      message.success("Сообщение отправлено");
      onClose();
    } catch (err) {
      message.error(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const renderChannelSelector = () => (
    <Space orientation="vertical" style={{ width: "100%" }}>
      <Checkbox.Group
        options={[
          {
            label: (
              <Space>
                <MailOutlined />
                Email
              </Space>
            ),
            value: "email",
          },
          {
            label: (
              <Space>
                <SendOutlined />
                Telegram
              </Space>
            ),
            value: "telegram",
            disabled: !hasTelegram,
          },
        ]}
        value={channels}
        onChange={(value) => setChannels(value as NotificationChannel[])}
      />
      {hasTelegram ? (
        <Tag>
          Telegram:{" "}
          {defaultTelegramUsername
            ? `@${defaultTelegramUsername}`
            : `id: ${defaultTelegramId ?? "—"}`}
        </Tag>
      ) : (
        <Tooltip title="Телеграм не привязан к пользователю">
          <Tag color="default">Telegram недоступен</Tag>
        </Tooltip>
      )}
    </Space>
  );

  return (
    <DrawerForm<MailSendPayload>
      open={open}
      onClose={onClose}
      title="Отправка сообщения"
      size="small"
      onSubmit={handleSend}
      initialValues={{
        to: defaultEmail,
        subject: defaultSubject ?? "",
        body: defaultBody ?? "",
      }}
      submitText="Отправить"
      cancelText="Отменить"
      loading={loading}
      disabled={!channels.length}
      destroyOnHidden
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        {renderChannelSelector()}
      </Space>
      <div style={{ marginTop: 16 }} />
      <Form.Item
        name="to"
        label="Получатель"
        rules={[
          { required: emailRequired, message: "Укажите email" },
          { type: "email", message: "Некорректный email" },
        ]}
      >
        <Input placeholder="user@example.com" disabled={!channels.includes("email")} />
      </Form.Item>
      <Form.Item
        name="subject"
        label="Тема"
        rules={emailRequired ? [{ required: true, message: "Укажите тему" }] : undefined}
      >
        <Input placeholder="Тема письма" />
      </Form.Item>
      <Form.Item
        name="body"
        label="Сообщение"
        rules={[{ required: true, message: "Введите текст" }]}
      >
        <Input.TextArea rows={6} placeholder="Текст сообщения" />
      </Form.Item>
    </DrawerForm>
  );
}
